'use client';

import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {p, pr, py, pl} from '@nib/layout';
import {
  copyColor,
  copyFontFamily,
  copyFontSizeSmall,
  formInputBackgroundColor,
  formBorderColor,
  formBorderColorValid,
  formBorderColorInvalid,
  focusOutlineColor,
  focusOutlineStyle,
  focusOutlineWidth,
  focusOutlineOffset
} from '@nib-components/theme';

const disabledCursor = 'not-allowed';
const disabledOpacity = 0.5;

const validFormModes = ['light', 'white'] as const;
type FormModes = (typeof validFormModes)[number];

interface SelectInputProps {
  isCompact?: boolean;
  valid?: boolean;
  validated?: boolean;
  formMode?: FormModes;
  [key: string]: unknown;
}

export const SelectInput = styled.select<SelectInputProps>`
  display: inline-block;
  ${py(4)};
  ${pl(4)};
  /* Non-standard padding to accommodate validation icon */
  padding-right: 3.5rem;

  font-size: 1rem;
  font-family: ${copyFontFamily};
  color: ${copyColor};
  line-height: 1.5;

  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0;
  border: none;
  border-radius: 0;
  box-shadow: inset 0 -2px 0 0 ${formBorderColor};
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  background-color: ${formInputBackgroundColor};
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0nMjRweCcgaGVpZ2h0PScyNHB4JyB2aWV3Qm94PScwIDAgMjQgMjQnIHZlcnNpb249JzEuMScgeG1sbnM9J2h0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnJyB4bWxuczp4bGluaz0naHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayc+PHRpdGxlPmljb24vc3lzdGVtL2Fycm93X2Rvd248L3RpdGxlPjxnIGlkPSdpY29uL3N5c3RlbS9hcnJvd19kb3duJyBzdHJva2U9J25vbmUnIHN0cm9rZS13aWR0aD0nMScgZmlsbD0nbm9uZScgZmlsbC1ydWxlPSdldmVub2RkJz48cGF0aCBkPSdNNy4zODc4MTM2LDEyLjEzNTM2MzcgTDE4LjAwNzQ4MzUsMS41MTU2OTM4MSBDMTguMzAwMzc2NywxLjIyMjgwMDU5IDE4LjMwMDM3NjcsMC43NDc5MjY4NTggMTguMDA3NDgzNSwwLjQ1NTAzMzY0IEMxNy43MTQ1OTAzLDAuMTYyMTQwNDIxIDE3LjIzOTcxNjYsMC4xNjIxNDA0MjEgMTYuOTQ2ODIzMywwLjQ1NTAzMzY0IEw1Ljc5NjgyMzM0LDExLjYwNTAzMzYgQzUuNTAzOTMwMTIsMTEuODk3OTI2OSA1LjUwMzkzMDEyLDEyLjM3MjgwMDYgNS43OTY4MjMzNCwxMi42NjU2OTM4IEwxNi45NDY4MjMzLDIzLjgxNTY5MzggQzE3LjIzOTcxNjYsMjQuMTA4NTg3IDE3LjcxNDU5MDMsMjQuMTA4NTg3IDE4LjAwNzQ4MzUsMjMuODE1NjkzOCBDMTguMzAwMzc2NywyMy41MjI4MDA2IDE4LjMwMDM3NjcsMjMuMDQ3OTI2OSAxOC4wMDc0ODM1LDIyLjc1NTAzMzYgTDcuMzg3ODEzNiwxMi4xMzUzNjM3IFonIGZpbGw9JyM0NDQ0NDQnIGZpbGwtcnVsZT0nbm9uemVybycgdHJhbnNmb3JtPSd0cmFuc2xhdGUoMTEuOTAyMTUzLCAxMi4xMzUzNjQpIHJvdGF0ZSgtOTAuMDAwMDAwKSB0cmFuc2xhdGUoLTExLjkwMjE1MywgLTEyLjEzNTM2NCkgJz48L3BhdGg+PC9nPjwvc3ZnPg==');
  background-repeat: no-repeat;
  background-position: right 1rem top 50%;
  background-size: 1.25em auto;
  cursor: pointer;

  &::-ms-expand {
    display: none;
  }

  ${({isCompact}) =>
    isCompact &&
    css`
      ${p(2)};
      ${pr(7)};
      font-size: ${copyFontSizeSmall};
    `};

  ${({disabled}) =>
    disabled &&
    css`
      opacity: ${disabledOpacity};
      cursor: ${disabledCursor};
    `}

  /* Validated and invalid */
  ${({valid, validated}) =>
    !valid &&
    validated &&
    css`
      box-shadow: inset 0 0 0 2px ${formBorderColorInvalid};
    `}

  /* Validated and valid */
  ${({valid, validated}) =>
    valid &&
    validated &&
    css`
      box-shadow: inset 0 -2px 0 0 ${formBorderColorValid};
    `}

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: ${focusOutlineOffset};
  }
`;

interface SelectProps extends SelectInputProps {
  placeholder?: string;
  options?: {[key: string]: string} | {[key: string]: string}[];
  disabled?: boolean;
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * Render a select input
 * @param   {object} props
 * @returns {ReactElement}
 */
const Select = (props: SelectProps): JSX.Element => {
  const {placeholder, options, disabled, valid, validated, formMode, children, ...otherProps} = props;

  let optionsArray = options;
  if (options && !Array.isArray(options)) {
    optionsArray = Object.keys(options).map(key => ({value: key, label: options[key]}));
  }

  return (
    <SelectInput disabled={disabled} valid={valid} validated={validated} formMode={formMode} {...otherProps}>
      {placeholder && <option value="">{placeholder}</option>}
      {options
        ? (optionsArray as {[key: string]: string}[]).map((element: {[key: string]: string}, index: number) => (
            <option key={index} value={element.value}>
              {element.label}
            </option>
          ))
        : children}
    </SelectInput>
  );
};

Select.displayName = 'Select';
Select.SelectInput = SelectInput;

Select.propTypes = {
  placeholder: PropTypes.string,
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string
      })
    ),
    PropTypes.objectOf(PropTypes.string)
  ]),
  disabled: PropTypes.bool,
  valid: PropTypes.bool,
  validated: PropTypes.bool,
  formMode: PropTypes.oneOf(validFormModes),
  children: PropTypes.node
};

Select.defaultProps = {
  valid: false,
  validated: false,
  disabled: false
};

export default Select;
